import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import "./Sidebar.css";
import {
  FaHome,
  FaBullhorn,
  FaEnvelope,
  FaUsers,
  FaCalendarAlt,
  FaBook,
  FaBars,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaBookOpen,
} from "react-icons/fa";
import { IoLogOut } from "react-icons/io5";
import { getDecryptedUserId } from "../../utils/cryptoUtils";

const menuItems = [
  {
    icon: <FaHome />,
    label: "Home",
    path: "/home",
  },
  {
    icon: <FaBullhorn />,
    label: "Announcements",
    path: "/announcements",
  },
  {
    icon: <FaEnvelope />,
    label: "Send Message",
    path: "/push-message",
  },
  { icon: <FaUsers />, label: "View Members", path: "/view-members" },
  { icon: <FaCalendarAlt />, label: "Add Events", path: "/addevents" },
  { icon: <FaBook />, label: "Handle Bookings", path: "/handlebookings" },
  {
    icon: <FaBookOpen />,
    label: "Useful Resources",
    path: "/usefulresources",
  },
  { icon: <IoLogOut />, label: "Log Out", isLogout: true },
];

export default function Sidebar({ onLogout = async () => {} }) {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("/");
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Desktop collapse state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });

  // Update CSS variable when collapsed state changes
  useEffect(() => {
    const root = document.documentElement;
    if (isCollapsed) {
      root.style.setProperty(
        "--sidebar-current-width",
        "var(--sidebar-collapsed-width, 70px)",
      );
    } else {
      root.style.setProperty(
        "--sidebar-current-width",
        "var(--sidebar-width, 200px)",
      );
    }
    localStorage.setItem("sidebarCollapsed", isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    const fetchUserCommittees = async () => {
      try {
        // Get user ID from localStorage (decrypt)
        const userId = await getDecryptedUserId();

        //console.log('Starting fetch for user:', userId);

        if (!userId) {
          setLoading(false);
          return;
        }

        const url =
          process.env.REACT_APP_BACKEND_URL +
          `/committees/memberships/?user=${userId}`;
        //console.log('Fetching from URL:', url);

        const response = await fetch(url);

        //console.log('Response status:', response.status);
        //console.log('Response OK:', response.ok);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch committees (Status: ${response.status})`,
          );
        }

        const data = await response.json();
        //console.log('Raw data received:', data);

        // Transform the data to match your UI structure
        const formattedCommittees = data
          .filter((membership) => membership.role !== "Member")
          .map((membership) => ({
            id: membership.id,
            committeeId: membership.committee.committee_id,
            name: membership.committee.name,
            role: membership.role,
          }));

        //console.log('Formatted committees:', formattedCommittees);
        setCommittees(formattedCommittees);
        setLoading(false);
      } catch (err) {
        console.error("Full error details:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchUserCommittees();
  }, []);

  const handleSelect = async (item) => {
    if (item.isLogout) {
      try {
        await onLogout?.();
      } finally {
        navigate("/", { replace: true });
      }
      return;
    }
    setActiveItem(item.path); //changes the active item to current selected item
    //console.log(`${item.label} selected`);
    //console.log(`Navigate to: ${item.path}`);
    navigate(item.path);
    setIsMobileMenuOpen(false); // Close menu on mobile after selection
  };

  const handleCommitteeClick = (committee) => {
    // Add your committee selection logic here
    //console.log(`Selected committee: ${committee.name} (ID: ${committee.committeeId})`);
    setIsMobileMenuOpen(false); // Close menu on mobile after selection
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Hamburger button - visible only on mobile */}
      <button
        className="hamburger-btn"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div className="sidebar-overlay" onClick={toggleMobileMenu}></div>
      )}

      <div
        className={`sidebar ${isMobileMenuOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}
      >
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            <span className="title-right">NEXUS</span>
            <span className="title-sub">Portal</span>
          </h2>
          {/* Collapse toggle button - visible on desktop/tablet */}
          <button
            className="collapse-btn"
            onClick={toggleCollapse}
            aria-label="Toggle sidebar"
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item, index) => (
            <div
              onClick={() => handleSelect(item)}
              key={index}
              className={`nav-item ${activeItem === item.path ? "active" : ""}`}
              title={isCollapsed ? item.label : ""}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </div>
          ))}
        </nav>

        <div className="team-section">
          <h3>My Committees</h3>
          {loading && <p className="loading-text">Loading...</p>}
          {error && <p className="error-text">Error: {error}</p>}
          {!loading && !error && committees.length === 0 && (
            <p className="no-committees">No committees found</p>
          )}
          {!loading &&
            !error &&
            committees.map((committee) => (
              <button
                key={committee.id}
                className="team-member-button"
                onClick={() => handleCommitteeClick(committee)}
                title={isCollapsed ? committee.name : ""}
              >
                <span className="member-name">{committee.name}</span>
                <span className="member-role">{committee.role}</span>
              </button>
            ))}
        </div>
      </div>
    </>
  );
}
